import React from 'react';
import { useI18n } from '../../../i18n/index.jsx';

// Fields generated from the category's spec template (Admin > Catégories):
// dropdown / chips / number + unit / text. Values are kept as lists of
// strings per field key (French option = stored value); numbers as
// "8.35 mm". Short lists show as tap chips, long ones as a dropdown.
const CHIP_MAX = 8;

export const label = (f, lang) => (lang === 'en' ? f.labelEn || f.labelFr : f.labelFr || f.labelEn);
const optLabel = (o, lang) => (lang === 'en' ? o.en || o.fr : o.fr);

// "8.35 mm" → { n: '8.35', unit: 'mm' }
export function splitNumber(raw, units) {
  const s = String(raw || '').trim();
  const u = units.find((x) => s.endsWith(` ${x}`));
  return { n: u ? s.slice(0, -u.length - 1) : s, unit: u || units[0] || '' };
}

export default function SpecFields({ fields, values, onChange, errors = {} }) {
  const { t, lang } = useI18n();
  if (!fields.length) return null;
  const set = (key, list) => onChange({ ...values, [key]: list });
  return (
    <div className="wz-specs">
      {fields.map((f) => {
        const cur = values[f.key] || [];
        const id = `wz-spec-${f.key}`;
        const head = <>{label(f, lang)}{f.required ? <span className="req" aria-hidden="true"> *</span> : <span className="field-optional"> ({t('common.optional')})</span>}</>;
        const err = errors[f.key] && <p className="field-error" role="alert">{errors[f.key]}</p>;
        if (f.kind === 'select' || f.kind === 'chips') {
          const multi = f.kind === 'chips';
          if (!multi && f.options.length > CHIP_MAX) {
            return (
              <div className="field" key={f.key}>
                <label htmlFor={id}>{head}</label>
                <select id={id} className={errors[f.key] ? 'is-invalid' : undefined} value={cur[0] || ''} onChange={(e) => set(f.key, e.target.value ? [e.target.value] : [])}>
                  <option value="">{t('profile.choose')}</option>
                  {f.options.map((o) => <option key={o.fr} value={o.fr}>{optLabel(o, lang)}</option>)}
                </select>
                {err}
              </div>
            );
          }
          return (
            <div className="field" key={f.key} role="group" aria-labelledby={`${id}-l`}>
              <span className="field-label" id={`${id}-l`}>{head}{multi && <span className="field-optional"> · {t('wizard.severalOk')}</span>}</span>
              <div className="chip-choice">
                {f.options.map((o) => {
                  const on = cur.includes(o.fr);
                  return (
                    <button key={o.fr} type="button" className={`chip-toggle${on ? ' on' : ''}`} aria-pressed={on}
                      onClick={() => set(f.key, multi ? (on ? cur.filter((x) => x !== o.fr) : [...cur, o.fr]) : (on ? [] : [o.fr]))}>
                      {optLabel(o, lang)}
                    </button>
                  );
                })}
              </div>
              {err}
            </div>
          );
        }
        if (f.kind === 'number') {
          const { n, unit } = splitNumber(cur[0], f.units);
          const put = (nv, uv) => set(f.key, String(nv).trim() ? [`${String(nv).trim()}${uv ? ` ${uv}` : ''}`] : []);
          return (
            <div className="field" key={f.key}>
              <label htmlFor={id}>{head}</label>
              <div className="wz-number">
                <input id={id} className={errors[f.key] ? 'is-invalid' : undefined} inputMode="decimal" value={n} onChange={(e) => put(e.target.value, unit)} placeholder="0" />
                {f.units.length > 1
                  ? <select aria-label={t('wizard.unitOf', { field: label(f, lang) })} value={unit} onChange={(e) => put(n, e.target.value)}>{f.units.map((u) => <option key={u}>{u}</option>)}</select>
                  : f.units[0] && <span className="wz-unit">{f.units[0]}</span>}
              </div>
              {err}
            </div>
          );
        }
        return (
          <div className="field" key={f.key}>
            <label htmlFor={id}>{head}</label>
            <input id={id} className={errors[f.key] ? 'is-invalid' : undefined} value={cur[0] || ''} maxLength={120} onChange={(e) => set(f.key, e.target.value ? [e.target.value] : [])} />
            {err}
          </div>
        );
      })}
    </div>
  );
}

// Template values → API catalogSpecs [{ groupId, value, unit }].
export function specsToBody(fields, values) {
  const out = [];
  for (const f of fields) {
    for (const v of values[f.key] || []) {
      if (!String(v).trim()) continue;
      if (f.kind === 'number') {
        const { n, unit } = splitNumber(v, f.units);
        if (n) out.push({ groupId: f.key, value: n, unit });
      } else out.push({ groupId: f.key, value: v });
    }
  }
  return out;
}

// Inline check: required fields, numbers.
export function checkSpecs(fields, values, t, lang) {
  const e = {};
  for (const f of fields) {
    const cur = (values[f.key] || []).filter((v) => String(v).trim());
    if (f.required && !cur.length) e[f.key] = t('wizard.required', { field: label(f, lang) });
    if (f.kind === 'number' && cur.length) {
      const { n } = splitNumber(cur[0], f.units);
      if (!Number.isFinite(Number(n.replace(',', '.').replace(/\s/g, '')))) e[f.key] = t('errors.invalid_spec_number');
    }
  }
  return e;
}
